// src/pages/Leads/PipelineView.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  TrendingUp,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Call as CallIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import apiClient from '../../api/client';
import { formatCurrency as formatCurrencyUtil } from '../../utils/currency';

const PipelineView = () => {
  const navigate = useNavigate();

  // Fetch pipeline stages data
  const { data: pipelineStages = [], isLoading: stagesLoading, error: stagesError } = useQuery({
    queryKey: ['leads-pipeline-stages'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/leads/pipeline/stages');
        return response.data || [];
      } catch (error) {
        console.error('Error fetching pipeline stages:', error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch all leads for detailed pipeline view
  const { data: leadsData, isLoading: leadsLoading, error: leadsError } = useQuery({
    queryKey: ['leads-for-pipeline'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/leads?limit=100');
        return response.data.leads || [];
      } catch (error) {
        console.error('Error fetching leads for pipeline:', error);
        return [];
      }
    },
    staleTime: 2 * 60 * 1000,
  });

  const formatCurrency = (amount) => formatCurrencyUtil(amount);

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'primary';
      case 'won':
        return 'success';
      case 'lost':
        return 'error';
      default:
        return 'default';
    }
  };

  if (stagesError || leadsError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load pipeline data. Please try again later.
        </Alert>
      </Box>
    );
  }

  // Group leads by status for pipeline visualization
  const leadsByStatus = (leadsData || []).reduce((acc, lead) => {
    const status = lead.status || 'Unknown';
    if (!acc[status]) acc[status] = [];
    acc[status].push(lead);
    return acc;
  }, {});

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate('/leads')} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" gutterBottom>
            Sales Pipeline
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Visual pipeline view of all leads by status
          </Typography>
        </Box>
      </Box>

      {/* Pipeline Stages Overview */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Pipeline Overview
        </Typography>
        {stagesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : pipelineStages.length === 0 ? (
          <Alert severity="info">
            No pipeline stages available
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {pipelineStages.map((stage, index) => (
              <Grid item xs={12} sm={6} md={3} key={stage.id || index}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6" color="primary">
                        {stage.name}
                      </Typography>
                      <Chip
                        label={`${stage.conversion_probability || 0}%`}
                        color={stage.conversion_probability > 75 ? 'success' : stage.conversion_probability > 50 ? 'warning' : 'default'}
                        size="small"
                      />
                    </Box>
                    
                    <Typography variant="h4" gutterBottom>
                      {stage.lead_count || 0}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {formatCurrency(stage.total_value || 0)} total value
                    </Typography>
                    
                    <LinearProgress
                      variant="determinate"
                      value={stage.conversion_probability || 0}
                      sx={{ mt: 2, height: 6, borderRadius: 3 }}
                      color={stage.conversion_probability > 75 ? 'success' : stage.conversion_probability > 50 ? 'warning' : 'primary'}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Detailed Pipeline View */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Leads by Status
        </Typography>
        
        {leadsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : leadsData.length === 0 ? (
          <Alert severity="info">
            No leads available for pipeline view
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {Object.entries(leadsByStatus).map(([status, leads]) => (
              <Grid item xs={12} md={6} lg={4} key={status}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">
                        {status}
                      </Typography>
                      <Chip
                        label={leads.length}
                        color={getStatusColor(status)}
                        size="small"
                      />
                    </Box>
                    
                    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                      {leads.slice(0, 10).map((lead) => (
                        <Box
                          key={lead.id}
                          sx={{
                            p: 2,
                            mb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="subtitle2">
                              {`${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unnamed Lead'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    // Show lead details in an alert for now (read-only)
                                    alert(`Lead Details:\n\nName: ${lead.first_name || ''} ${lead.last_name || ''}\nEmail: ${lead.email || 'N/A'}\nPhone: ${lead.phone || lead.mobile || 'N/A'}\nStatus: ${lead.status || 'N/A'}\nSource: ${lead.source || 'N/A'}\nValue: ${formatCurrency(lead.estimated_value)}\n\nDetailed lead view page coming soon!`);
                                  }}
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {(lead.phone || lead.mobile) && (
                                <Tooltip title="Call">
                                  <IconButton
                                    size="small"
                                    onClick={() => window.open(`tel:${lead.phone || lead.mobile}`)}
                                  >
                                    <CallIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {lead.email && (
                                <Tooltip title="Email">
                                  <IconButton
                                    size="small"
                                    onClick={() => window.open(`mailto:${lead.email}`)}
                                  >
                                    <EmailIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {formatCurrency(lead.estimated_value)}
                          </Typography>
                          
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(lead.created_at)}
                          </Typography>
                          
                          {lead.source && (
                            <Chip
                              label={lead.source}
                              size="small"
                              variant="outlined"
                              sx={{ mt: 1, fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                        </Box>
                      ))}
                      
                      {leads.length > 10 && (
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                          ... and {leads.length - 10} more leads
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default PipelineView;